/** FieldValueInput — one editor for an AttributeDef, shared by bulk set-field and inline edit. */
import React from 'react'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import { RefFieldWidget } from './RefFieldWidget'
import { DateTimeField } from '../shared/DateTimeField'
import { resolveDateTimeFieldMode } from '../shared/fieldInputTypes'
import { FormattedNumberField, resolveNumberDigits } from './number'
import type { AttributeDef } from '../designer/services/configObjectApi'
import { clampToAttrMaxLength, resolveAttrMaxLength } from '@/shared/resolveAttrMaxLength'

const isRef = (t: string) => /ref|reference|enum/.test(t.toLowerCase())
const isNum = (t: string) => /number|int|float|decimal|numeric/.test(t.toLowerCase())
const isBool = (t: string) => /bool/.test(t.toLowerCase())

export function FieldValueInput({ attr, value, onChange, connectionId, autoFocus, disabled }: {
  attr: AttributeDef; value: unknown; onChange: (v: unknown) => void; connectionId: string; autoFocus?: boolean; disabled?: boolean
}) {
  const type = (attr.attr_type || 'string').toLowerCase()
  const format = (attr.props?.format as string | undefined) ?? undefined
  const maxLength = resolveAttrMaxLength(attr)
  if (isRef(type)) return <RefFieldWidget attr={attr} value={value} onChange={onChange} disabled={!!disabled} connectionId={connectionId} isRequired={false} />
  if (isBool(type)) return <Checkbox size="small" checked={value === true || value === 1 || value === '1'} disabled={disabled} onChange={(e) => onChange(e.target.checked)} autoFocus={autoFocus} />
  if (isNum(type)) {
    const { precision, scale } = resolveNumberDigits(attr.props)
    return (
      <FormattedNumberField
        value={value == null || value === '' ? null : Number(value)}
        onChange={onChange}
        precision={precision}
        scale={scale}
        disabled={disabled}
        autoFocus={autoFocus}
        size="small"
      />
    )
  }
  const dateMode = resolveDateTimeFieldMode(type, format)
  if (dateMode) {
    return (
      <DateTimeField
        mode={dateMode}
        attrType={type}
        format={format}
        value={value}
        onChange={(next) => onChange(next)}
        disabled={disabled}
        autoFocus={autoFocus}
        size="small"
        fullWidth={false}
      />
    )
  }
  return (
    <TextField
      size="small"
      value={value ?? ''}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(e) => onChange(clampToAttrMaxLength(attr, e.target.value))}
      inputProps={{ maxLength: maxLength ?? undefined }}
    />
  )
}
